<template>
  <div class="bl-page">
      <header class="bl-header">
        <div class="bl-header-inner">
          <h1 class="bl-title">Kiey Business Logic Reference</h1>
          <p class="bl-subtitle">{{ metadata.description }}</p>
        </div>
      </header>

      <div class="bl-filters">
        <div class="bl-filters-inner">
          <div class="bl-filter-row">
            <div class="bl-filter-group">
              <label>Role</label>
              <div class="bl-multiselect" @click="toggleDropdown('role')">
                <span class="bl-multiselect-text">{{ selectedRoles.length ? selectedRoles.map(r => getRoleLabel(r)).join(', ') : 'All Roles' }}</span>
                <svg class="bl-multiselect-arrow" width="10" height="6" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="#8a8f98" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              <div v-if="openDropdown === 'role'" class="bl-dropdown">
                <div v-for="role in availableRoles" :key="role.id" class="bl-dropdown-item" @click.stop="toggleCheckbox('selectedRoles', role.id); onRoleChange()">
                  <input type="checkbox" :checked="selectedRoles.includes(role.id)" @click.prevent />
                  <span>{{ role.label }}</span>
                </div>
              </div>
            </div>

            <div class="bl-filter-group">
              <label>Platform</label>
              <div class="bl-multiselect" @click="toggleDropdown('platform')">
                <span class="bl-multiselect-text">{{ selectedPlatforms.length ? selectedPlatforms.map(p => getPlatformLabel(p)).join(', ') : 'All Platforms' }}</span>
                <svg class="bl-multiselect-arrow" width="10" height="6" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="#8a8f98" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              <div v-if="openDropdown === 'platform'" class="bl-dropdown">
                <div v-for="plat in availablePlatforms" :key="plat.id" class="bl-dropdown-item" @click.stop="toggleCheckbox('selectedPlatforms', plat.id); onPlatformChange()">
                  <input type="checkbox" :checked="selectedPlatforms.includes(plat.id)" @click.prevent />
                  <span>{{ plat.label }}</span>
                </div>
              </div>
            </div>

            <div class="bl-filter-group">
              <label>Topic</label>
              <div class="bl-multiselect" @click="toggleDropdown('topic')">
                <span class="bl-multiselect-text">{{ selectedTopics.length ? selectedTopics.join(', ') : 'All Topics' }}</span>
                <svg class="bl-multiselect-arrow" width="10" height="6" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="#8a8f98" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              <div v-if="openDropdown === 'topic'" class="bl-dropdown">
                <div v-for="topic in availableTopics" :key="topic" class="bl-dropdown-item" @click.stop="toggleCheckbox('selectedTopics', topic); onTopicChange()">
                  <input type="checkbox" :checked="selectedTopics.includes(topic)" @click.prevent />
                  <span>{{ topic }}</span>
                </div>
              </div>
            </div>

            <div class="bl-filter-group">
              <label>Feature</label>
              <div class="bl-multiselect" @click="toggleDropdown('feature')">
                <span class="bl-multiselect-text">{{ selectedFeatures.length ? selectedFeatures.join(', ') : 'All Features' }}</span>
                <svg class="bl-multiselect-arrow" width="10" height="6" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="#8a8f98" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              <div v-if="openDropdown === 'feature'" class="bl-dropdown">
                <div v-for="feat in availableFeatures" :key="feat" class="bl-dropdown-item" @click.stop="toggleCheckbox('selectedFeatures', feat)">
                  <input type="checkbox" :checked="selectedFeatures.includes(feat)" @click.prevent />
                  <span>{{ feat }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="bl-search-row">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search entries by title, summary, or details..."
              class="bl-search-input"
            />
            <span class="bl-result-count">{{ filteredEntries.length }} {{ filteredEntries.length === 1 ? 'entry' : 'entries' }}</span>
          </div>
        </div>
      </div>

      <div class="bl-content">
        <div v-if="filteredEntries.length === 0" class="bl-empty">
          <p>No entries match the selected filters.</p>
        </div>

        <div
          v-for="entry in filteredEntries"
          :key="entry.id"
          class="bl-card"
          :class="{ 'bl-card-expanded': expandedIds[entry.id] }"
        >
          <div class="bl-card-header" @click="toggleExpand(entry.id)">
            <div class="bl-card-title-row">
              <h3 class="bl-card-title">{{ entry.title }}</h3>
              <svg
                class="bl-card-chevron"
                :class="{ 'bl-card-chevron-open': expandedIds[entry.id] }"
                width="16" height="16" viewBox="0 0 16 16"
              >
                <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <p class="bl-card-summary">{{ entry.summary }}</p>
            <div class="bl-card-tags">
              <span
                v-for="role in entry.roles"
                :key="'r-' + role"
                class="bl-tag bl-tag-role"
              >{{ getRoleLabel(role) }}</span>
              <span
                v-for="plat in entry.platforms"
                :key="'p-' + plat"
                class="bl-tag bl-tag-platform"
              >{{ getPlatformLabel(plat) }}</span>
              <span class="bl-tag bl-tag-topic">{{ entry.topic }}</span>
              <span class="bl-tag bl-tag-feature">{{ entry.feature }}</span>
            </div>
          </div>

          <div v-if="expandedIds[entry.id]" class="bl-card-body">
            <div v-if="entry.business_summary" class="bl-card-section">
              <h4 class="bl-section-label bl-section-business-label">Business</h4>
              <div class="bl-card-details">
                <p>{{ entry.business_summary }}</p>
              </div>
            </div>

            <div class="bl-card-section bl-card-section-technical">
              <h4 class="bl-section-label bl-section-technical">Technical</h4>
              <div class="bl-card-details">
                <p>{{ entry.details }}</p>
              </div>

              <div v-if="entry.values && Object.keys(entry.values).length" class="bl-card-values">
                <h4>Values</h4>
                <table>
                  <tr v-for="(val, key) in entry.values" :key="key">
                    <td class="bl-val-key">{{ key }}</td>
                    <td class="bl-val-val">{{ val }}</td>
                  </tr>
                </table>
              </div>

              <div v-if="entry.related && entry.related.length" class="bl-card-related">
                <h4>Related</h4>
                <div class="bl-related-links">
                  <span
                    v-for="relId in entry.related"
                    :key="relId"
                    class="bl-related-link"
                    @click.stop="scrollToEntry(relId)"
                  >{{ getEntryTitle(relId) || relId }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      </div>
</template>

<script>
import staticData from './data.json';
import { firestore } from '@/config/firebase';

export default {
  name: 'BusinessLogicReference',
  mounted() {
    document.addEventListener('click', this.closeDropdowns);
    this.loadFromFirestore();
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeDropdowns);
  },
  data() {
    return {
      metadata: staticData.metadata,
      allEntries: staticData.entries,
      filters: staticData.filters,
      selectedRoles: [],
      selectedPlatforms: [],
      selectedTopics: [],
      selectedFeatures: [],
      searchQuery: '',
      expandedIds: {},
      openDropdown: null,
    };
  },
  computed: {
    availableRoles() {
      return this.metadata.roles.filter(r => r.id !== 'all');
    },
    availablePlatforms() {
      if (!this.selectedRoles.length) {
        return this.metadata.platforms;
      }
      const platformIds = new Set();
      const allFilters = this.filters['all'] || {};
      for (const p of Object.keys(allFilters)) platformIds.add(p);
      for (const role of this.selectedRoles) {
        const roleFilters = this.filters[role] || {};
        for (const p of Object.keys(roleFilters)) platformIds.add(p);
      }
      return this.metadata.platforms.filter(p => platformIds.has(p.id));
    },
    availableTopics() {
      const topics = new Set();
      const roles = this.selectedRoles.length ? ['all', ...this.selectedRoles] : Object.keys(this.filters);
      const platforms = this.selectedPlatforms.length ? this.selectedPlatforms : null;
      for (const roleKey of roles) {
        const roleFilters = this.filters[roleKey];
        if (!roleFilters) continue;
        const platKeys = platforms || Object.keys(roleFilters);
        for (const plat of platKeys) {
          if (roleFilters[plat]) {
            for (const t of Object.keys(roleFilters[plat])) topics.add(t);
          }
        }
      }
      return [...topics].sort();
    },
    availableFeatures() {
      if (!this.selectedTopics.length) return [];
      const features = new Set();
      const roles = this.selectedRoles.length ? ['all', ...this.selectedRoles] : Object.keys(this.filters);
      const platforms = this.selectedPlatforms.length ? this.selectedPlatforms : null;
      for (const roleKey of roles) {
        const roleFilters = this.filters[roleKey];
        if (!roleFilters) continue;
        const platKeys = platforms || Object.keys(roleFilters);
        for (const plat of platKeys) {
          if (roleFilters[plat]) {
            for (const topic of this.selectedTopics) {
              if (roleFilters[plat][topic]) {
                for (const f of roleFilters[plat][topic]) features.add(f);
              }
            }
          }
        }
      }
      return [...features].sort();
    },
    filteredEntries() {
      let entries = this.allEntries;

      if (this.selectedRoles.length) {
        entries = entries.filter(e =>
          e.roles.includes('all') || e.roles.some(r => this.selectedRoles.includes(r))
        );
      }

      if (this.selectedPlatforms.length) {
        entries = entries.filter(e =>
          e.platforms.some(p => this.selectedPlatforms.includes(p))
        );
      }

      if (this.selectedTopics.length) {
        entries = entries.filter(e => this.selectedTopics.includes(e.topic));
      }

      if (this.selectedFeatures.length) {
        entries = entries.filter(e => this.selectedFeatures.includes(e.feature));
      }

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        entries = entries.filter(e =>
          e.title.toLowerCase().includes(q) ||
          e.summary.toLowerCase().includes(q) ||
          e.details.toLowerCase().includes(q) ||
          e.topic.toLowerCase().includes(q) ||
          e.feature.toLowerCase().includes(q)
        );
      }

      return entries;
    },
  },
  methods: {
    getAllTopics() {
      const topics = new Set();
      for (const roleObj of Object.values(this.filters)) {
        for (const platObj of Object.values(roleObj)) {
          for (const t of Object.keys(platObj)) topics.add(t);
        }
      }
      return [...topics].sort();
    },
    toggleDropdown(name) {
      this.openDropdown = this.openDropdown === name ? null : name;
    },
    toggleCheckbox(arrayName, value) {
      const idx = this[arrayName].indexOf(value);
      if (idx === -1) {
        this[arrayName].push(value);
      } else {
        this[arrayName].splice(idx, 1);
      }
    },
    closeDropdowns(e) {
      if (!e.target.closest('.bl-filter-group')) {
        this.openDropdown = null;
      }
    },
    onRoleChange() {
      this.selectedPlatforms = [];
      this.selectedTopics = [];
      this.selectedFeatures = [];
    },
    onPlatformChange() {
      this.selectedTopics = [];
      this.selectedFeatures = [];
    },
    onTopicChange() {
      this.selectedFeatures = [];
    },
    toggleExpand(id) {
      this.$set(this.expandedIds, id, !this.expandedIds[id]);
    },
    getRoleLabel(id) {
      const role = this.metadata.roles.find(r => r.id === id);
      return role ? role.label : id;
    },
    getPlatformLabel(id) {
      const plat = this.metadata.platforms.find(p => p.id === id);
      return plat ? plat.label : id;
    },
    getEntryTitle(id) {
      const entry = this.allEntries.find(e => e.id === id);
      return entry ? entry.title : null;
    },
    scrollToEntry(id) {
      this.$set(this.expandedIds, id, true);
      // Apply filters to show the entry
      const entry = this.allEntries.find(e => e.id === id);
      if (entry) {
        this.selectedRoles = [];
        this.selectedPlatforms = [];
        this.selectedTopics = [entry.topic];
        this.selectedFeatures = [entry.feature];
        this.searchQuery = '';
        this.$nextTick(() => {
          const cards = this.$el.querySelectorAll('.bl-card');
          for (const card of cards) {
            const title = card.querySelector('.bl-card-title');
            if (title && title.textContent === entry.title) {
              card.scrollIntoView({ behavior: 'smooth', block: 'center' });
              card.classList.add('bl-card-flash');
              setTimeout(() => card.classList.remove('bl-card-flash'), 1500);
              break;
            }
          }
        });
      }
    },
    async loadFromFirestore() {
      try {
        const doc = await firestore.collection('app_config').doc('business_logic').get();
        if (doc.exists) {
          const data = doc.data();
          if (data.entries && data.entries.length) {
            this.allEntries = data.entries;
            this.filters = data.filters || this.filters;
            if (data.metadata) {
              this.metadata = { ...this.metadata, ...data.metadata };
            }
          }
        }
      } catch (e) {
        // Firestore unavailable — fall back to static JSON (already loaded)
        // Firestore unavailable — using static JSON data
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$navy: #061D47;
$accent: #2c6bed;
$bg: #f5f6f8;
$card-bg: #ffffff;
$border: #e2e5ea;
$text-primary: #1b1b1b;
$text-secondary: #5e5e5e;
$text-muted: #8a8f98;
$radius: 10px;
$font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

.bl-page {
  font-family: $font;
  background: $bg;
  min-height: 100vh;
  color: $text-primary;
  -webkit-font-smoothing: antialiased;
}

.bl-header {
  background: $navy;
  color: #fff;
  padding: 16px 24px;
  margin: 12px 24px;
  border-radius: 12px;
}

.bl-header-inner {
  max-width: 1100px;
  margin: 0 auto;
}

.bl-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 6px;
  letter-spacing: -0.02em;
}

.bl-subtitle {
  font-size: 13px;
  margin: 0;
  opacity: 0.7;
  line-height: 1.5;
}

.bl-filters {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: 12px;
  margin: 0 24px;
  position: sticky;
  top: 12px;
  z-index: 50;
  overflow: visible;
}

.bl-filters-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 24px 16px;
}

.bl-filter-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  overflow: visible;
}

.bl-filter-group {
  flex: 1;
  min-width: 160px;
  position: relative;

  > label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: $text-muted;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 6px;
  }
}

.bl-multiselect {
  width: 100%;
  padding: 8px 28px 8px 12px;
  border: 1px solid $border;
  border-radius: 8px;
  font-size: 13px;
  font-family: $font;
  color: $text-primary;
  background: $bg;
  cursor: pointer;
  position: relative;
  min-height: 36px;
  display: flex;
  align-items: center;

  &:hover {
    border-color: darken($border, 10%);
  }
}

.bl-multiselect-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.bl-multiselect-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.bl-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  background: #fff;
  border: 1px solid $border;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 4px 0;
}

.bl-dropdown-item {
  display: flex !important;
  align-items: center;
  gap: 8px;
  padding: 6px 12px !important;
  cursor: pointer;
  font-size: 13px !important;
  line-height: 1.3 !important;
  color: $text-primary !important;
  font-weight: 400 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  margin: 0 !important;

  &:hover {
    background: rgba($accent, 0.06);
  }

  input[type="checkbox"] {
    width: 14px;
    height: 14px;
    accent-color: $accent;
    cursor: pointer;
    flex-shrink: 0;
    margin: 0;
  }

  span {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 13px !important;
  }
}

.bl-search-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.bl-search-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid $border;
  border-radius: 8px;
  font-size: 13px;
  font-family: $font;
  color: $text-primary;
  background: $bg;
  outline: none;

  &:focus {
    border-color: $accent;
    box-shadow: 0 0 0 2px rgba($accent, 0.15);
  }

  &::placeholder {
    color: $text-muted;
  }
}

.bl-result-count {
  font-size: 12px;
  color: $text-muted;
  white-space: nowrap;
}

.bl-content {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 24px 48px;
  position: relative;
  z-index: 1;
}

.bl-empty {
  text-align: center;
  padding: 60px 20px;
  color: $text-muted;
  font-size: 14px;
}

.bl-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  margin-bottom: 10px;
  overflow: hidden;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
}

.bl-card-flash {
  animation: cardFlash 1.5s ease;
}

@keyframes cardFlash {
  0%, 100% { background: $card-bg; }
  20% { background: rgba($accent, 0.08); }
}

.bl-card-header {
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
}

.bl-card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.bl-card-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: $text-primary;
  letter-spacing: -0.01em;
}

.bl-card-chevron {
  flex-shrink: 0;
  color: $text-muted;
  transition: transform 0.2s ease;
}

.bl-card-chevron-open {
  transform: rotate(180deg);
}

.bl-card-summary {
  font-size: 12.5px;
  color: $text-secondary;
  margin: 4px 0 0;
  line-height: 1.5;
}

.bl-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}

.bl-tag {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 0.02em;
}

.bl-tag-role {
  background: rgba($accent, 0.1);
  color: $accent;
}

.bl-tag-platform {
  background: rgba(#10b981, 0.1);
  color: #059669;
}

.bl-tag-topic {
  background: rgba($navy, 0.08);
  color: $navy;
}

.bl-tag-feature {
  background: rgba(#8b5cf6, 0.1);
  color: #7c3aed;
}

.bl-card-body {
  padding: 0 18px 16px;
  border-top: 1px solid $border;
  margin-top: 0;
}

.bl-card-section {
  margin-top: 0;
}

.bl-card-section-technical {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid $border;
}

.bl-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 14px 0 4px;
  padding: 3px 8px;
  border-radius: 4px;
  display: inline-block;
}

.bl-section-technical {
  background: rgba($navy, 0.08);
  color: $navy;
}

.bl-section-business-label {
  background: rgba(#10b981, 0.1);
  color: #059669;
  margin-top: 0;
}

.bl-card-details {
  margin-top: 8px;

  p {
    font-size: 12.5px;
    line-height: 1.7;
    color: $text-primary;
    margin: 0;
    white-space: pre-wrap;
  }
}

.bl-card-values {
  margin-top: 14px;

  h4 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $text-muted;
    margin: 0 0 6px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  tr {
    border-bottom: 1px solid rgba($border, 0.5);
  }

  td {
    padding: 5px 0;
    font-size: 12px;
  }

  .bl-val-key {
    font-weight: 500;
    color: $text-secondary;
    width: 200px;
    font-family: 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 11px;
  }

  .bl-val-val {
    color: $text-primary;
    font-family: 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 11px;
  }
}

.bl-card-related {
  margin-top: 14px;

  h4 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $text-muted;
    margin: 0 0 6px;
  }
}

.bl-related-links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.bl-related-link {
  font-size: 11.5px;
  color: $accent;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 4px;
  background: rgba($accent, 0.06);

  &:hover {
    background: rgba($accent, 0.12);
    text-decoration: underline;
  }
}

.bl-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: $bg;
  padding: 8px 40px;
  z-index: 60;
}

.bl-footer-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
}

.bl-footer-left,
.bl-footer-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.bl-footer-left span,
.bl-footer-right span {
  font-family: $font;
  font-size: 12px;
  font-weight: 500;
  color: $text-secondary;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .bl-filter-row {
    flex-direction: column;
    gap: 8px;
  }

  .bl-filter-group {
    min-width: 100%;
  }

  .bl-header-inner,
  .bl-filters-inner,
  .bl-content {
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>
